import { useState, useCallback, useEffect } from "react";
import { useTenant } from "../../../../hooks/useTenant";
import IntegrationService from "../../../../services/integration_service";
import AiWorkoutService from "../../../../services/ai_workout_service";
import { toast } from "react-toastify";

/**
 * Constantes para os formulários de geração de treino via IA
 */
export const AI_OBJECTIVES_BY_TYPE = {
    swimming: [
        { value: "aerobico", label: "Aeróbico (A1/A2)" },
        { value: "velocidade", label: "Velocidade (AN/VO2)" },
        { value: "resistencia", label: "Resistência (A2/A3)" },
        { value: "tecnica", label: "Técnica e Educativos" },
        { value: "misto", label: "Misto / Variado" },
        { value: "recuperacao", label: "Recuperação (Soltura)" },
    ],
    strength: [
        { value: "hipertrofia", label: "Hipertrofia" },
        { value: "forca", label: "Força Máxima" },
        { value: "emagrecimento", label: "Emagrecimento / Definição" },
        { value: "resistencia", label: "Resistência Muscular" },
        { value: "adaptacao", label: "Adaptação Anatômica" },
    ],
    running: [
        { value: "rodagem", label: "Rodagem (Endurance)" },
        { value: "tiros", label: "Tiros / Velocidade" },
        { value: "intervalado", label: "Intervalado / Fartlek" },
        { value: "regenerativo", label: "Regenerativo" },
    ],
    wod: [
        { value: "condicionamento", label: "Metcon (Condicionamento)" },
        { value: "forca", label: "Força / LPO" },
        { value: "gymnastics", label: "Ginástica (Gymnastics)" },
        { value: "endurance", label: "Endurance" },
    ]
};

export const AI_LEVELS = [
    { value: "iniciante", label: "Iniciante" },
    { value: "intermediario", label: "Intermediário" },
    { value: "avancado", label: "Avançado" },
    { value: "competicao", label: "Competição" },
];

export const AI_DURATIONS = [
    { value: 30, label: "30 minutos" },
    { value: 45, label: "45 minutos" },
    { value: 60, label: "60 minutos" },
    { value: 90, label: "90 minutos" },
    { value: 120, label: "120 minutos" },
];

const INITIAL_FORM = {
    workoutType: "swimming",
    objective: "aerobico",
    level: "intermediario",
    volume: 2000,
    duration: 60,
    styles: [],
    notes: "",
    daysPerWeek: 3,
    weeks: 4,
};

/**
 * useAiWorkout
 * Hook para gerenciar a geração de treinos via IA.
 */
export const useAiWorkout = () => {
    const { idTenant } = useTenant();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [formData, setFormData] = useState({ ...INITIAL_FORM });
    const [availableProviders, setAvailableProviders] = useState([]);
    const [loadingProviders, setLoadingProviders] = useState(false);
    const [generatedWorkout, setGeneratedWorkout] = useState(null);
    const [error, setError] = useState(null);

    /**
     * Carrega os providers de IA configurados e com API Key preenchida.
     */
    const loadAvailableProviders = useCallback(async () => {
        if (!idTenant) return;
        setLoadingProviders(true);
        try {
            const all = await IntegrationService.getAll(idTenant);
            const aiProviders = all
                .filter(
                    (i) =>
                        (i.provider === "openai" || i.provider === "gemini") &&
                        i.config?.apiKey
                )
                .map((i) => ({
                    id: i.provider,
                    name: i.provider === "openai" ? "OpenAI" : "Google Gemini",
                    model: i.config?.defaultModel || (i.provider === "openai" ? "gpt-4o-mini" : "gemini-2.0-flash"),
                }));
            setAvailableProviders(aiProviders);

            // Auto-selecionar se só tem um
            if (aiProviders.length === 1) {
                setFormData((prev) => ({ ...prev, provider: aiProviders[0].id }));
            }
        } catch (err) {
            console.error("Erro ao carregar providers de IA:", err);
        } finally {
            setLoadingProviders(false);
        }
    }, [idTenant]);

    // Carregar providers quando abre o modal
    useEffect(() => {
        if (isModalOpen) {
            loadAvailableProviders();
        }
    }, [isModalOpen, loadAvailableProviders]);

    const toggleModal = useCallback(() => {
        setIsModalOpen((prev) => {
            if (prev) {
                // Fechar: resetar estado
                setFormData({ ...INITIAL_FORM });
                setError(null);
                setGeneratedWorkout(null);
            }
            return !prev;
        });
    }, []);

    const updateFormField = useCallback((field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    }, []);

    const toggleStyle = useCallback((style) => {
        setFormData((prev) => {
            const styles = prev.styles.includes(style)
                ? prev.styles.filter((s) => s !== style)
                : [...prev.styles, style];
            return { ...prev, styles };
        });
    }, []);

    /**
     * Gera o treino via Cloud Function.
     * @returns {Object|null} O workout gerado ou null em caso de erro.
     */
    const handleGenerate = useCallback(async () => {
        setError(null);

        if (!formData.provider) {
            setError("Selecione um provider de IA.");
            return null;
        }
        if (!formData.workoutType || !formData.objective || !formData.level || !formData.volume) {
            setError("Preencha todos os campos obrigatórios.");
            return null;
        }
        if (Number(formData.volume) <= 0) {
            setError("Volume deve ser maior que zero.");
            return null;
        }

        setIsGenerating(true);
        try {
            const result = await AiWorkoutService.generate({
                provider: formData.provider,
                workoutType: formData.workoutType,
                objective: formData.objective,
                level: formData.level,
                volume: Number(formData.volume),
                duration: formData.duration ? Number(formData.duration) : null,
                styles: formData.styles.length > 0 ? formData.styles : null,
                notes: formData.notes || null,
                daysPerWeek: formData.daysPerWeek ? Number(formData.daysPerWeek) : null,
                weeks: formData.weeks ? Number(formData.weeks) : null,
            });

            if (result?.success && result?.workout) {
                setGeneratedWorkout(result.workout);
                toast.success("🤖 Treino gerado com sucesso!");
                return result.workout;
            } else {
                setError("Resposta inesperada do servidor.");
                return null;
            }
        } catch (err) {
            console.error("Erro ao gerar treino:", err);
            const msg = err?.message || err?.details?.message || "Erro ao gerar treino. Tente novamente.";
            setError(msg);
            toast.error(msg);
            return null;
        } finally {
            setIsGenerating(false);
        }
    }, [formData]);

    return {
        // Modal
        isModalOpen,
        toggleModal,
        // Form
        formData,
        updateFormField,
        toggleStyle,
        // Providers
        availableProviders,
        loadingProviders,
        // Generation
        isGenerating,
        generatedWorkout,
        error,
        handleGenerate,
    };
};
